import Vue from 'vue';
import Router from 'vue-router';
import store from '../store';

import Login from '../components/Login.vue';
import AuthenticatedLayout from '../layouts/AuthenticatedLayout.vue';

import Home from '../pages/Home.vue';
import Users from '../pages/Users.vue';
import Products from '../pages/Products.vue';
import AddUser from '../pages/AddUser.vue';
import AddProduct from '../pages/AddProduct.vue';
import AddFinalProduct from '../pages/AddFinalProduct.vue';
import AddProductDetails from '../pages/AddProductDetail.vue';
import AddCategory from '../pages/AddCategory.vue';
import AddSubcategory from '../pages/AddSubcategory.vue';
import Categories from '../pages/Categories.vue';
import Inventory from '../pages/Inventory.vue';
import SubCategories from '../pages/SubCategories.vue';
import NoPermission from '../pages/NoPermission.vue';
import ChangePassword from '../pages/ChangePassword.vue';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  routes: [
    { path: '/', redirect: '/login' },
    { path: '/login', component: Login },
    {
      path: '/dashboard',
      component: AuthenticatedLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: 'no-permission',
          component: NoPermission
        },
        { path: 'password', component: ChangePassword },
        { path: 'home', component: Home, meta: { permissionKey: 'home' } },
        { path: 'users', component: Users, meta: { permissionKey: 'users' } },
        { path: 'users/add', component: AddUser, meta: { permissionKey: 'users' } },
        { path: 'users/update/:id', component: AddUser, meta: { permissionKey: 'users' } },
        {
          path: 'categories', component: Categories,
          meta: { permissionKey: 'categories' }
        }, 
        {
          path: 'categories/add', component: AddCategory,
          meta: { permissionKey: 'categories' }
        },
        {
          path: 'categories/update/:id', component: AddCategory,
          meta: { permissionKey: 'categories' }
        },
        {
          path: 'categories/:id/subcategories', component: SubCategories,
          meta: { permissionKey: 'categories' }
        },
        {
          path: 'categories/:id/subcategories/add', component: AddSubcategory,
          meta: { permissionKey: 'categories' }
        },
        {
          path: 'categories/:id/subcategories/update/:subId', component: AddSubcategory,
          meta: { permissionKey: 'categories' }
        },
        { path: 'products', component: Products, meta: { permissionKey: 'products' }},
        { path: 'expired-products', component: () => import('../pages/ExpiredProduct.vue'), meta: { permissionKey: 'expiredProducts' }},
        {
          path: 'products/add', component: AddFinalProduct,
          meta: { permissionKey: 'products' }
        },
        {
          path: 'products/multiple', component: () => import('../pages/AddSheet.vue'),
          meta: { permissionKey: 'products' }
        },
        {
          path: 'products/sheets', component: () => import('../pages/SheetList.vue'),
          meta: { permissionKey: 'products' }
        },
        {
          path: 'products/update/:id', component: AddProduct,
          meta: { permissionKey: 'products' }
        },
        {
          path: 'products-details/add', component: AddProductDetails,
          meta: { permissionKey: 'products' }
        },
        {
          path: 'products/:id/details/view', component: () => import('../pages/ProductDetails.vue'),
          meta: { permissionKey: 'products' }
        },
        {
          path: 'products/:id/details/add', component: AddProductDetails,
          meta: { permissionKey: 'products' }
        },
        {
          path: 'products/:id/details/update/:detailId', component: AddProductDetails,
          meta: { permissionKey: 'products' }
        },
        {
          path: 'return-product',  component: () => import('../pages/ReturnProductList.vue'),
          meta: { permissionKey: 'returnProduct' }
        },
        {
          path: 'return-product/add',  component: () => import('../pages/ReturnProduct.vue'),
          meta: { permissionKey: 'returnProduct' }
        },
        {
          path: 'return-product/view/:id',  component: () => import('../pages/ReturnedItemDetail.vue'),
          meta: { permissionKey: 'returnProduct' }
        },
        {
          path: 'inventory', component: Inventory,
          meta: { permissionKey: 'inventory' }
        }, 
        { path: 'sell', component: () => import('../pages/Sell.vue'), meta: { permissionKey: 'sell' } },
        { path: 'sell/add', component: () => import('../pages/AddSell.vue'), meta: { permissionKey: 'sell' } },
        { path: 'sell/user', component: () => import('../pages/TotalUserSell.vue'), meta: { permissionKey: 'sell' } },
        { path: 'sell/view/:id', component: () => import('../pages/ViewInvoice.vue'), meta: { permissionKey: 'sell' } },
        { path: 'sell/update/:id', component: () => import('../pages/AddSell.vue'), meta: { permissionKey: 'sell' } },
        { path: 'reports', component: () => import('../pages/Report.vue'), meta: { permissionKey: 'reports' }},
        { path: 'reports/add', component: () => import('../pages/AddReport.vue'), meta: { permissionKey: 'reports' }},
        { path: 'expenses', component: () => import('../pages/Expenses.vue'), meta: { permissionKey: 'expenses' } },
        { path: 'expenses/add', component: () => import('../pages/AddExpenses.vue'), meta: { permissionKey: 'expenses' } },
        { path: 'expenses/update/:id', component: () => import('../pages/AddExpenses.vue'), meta: { permissionKey: 'expenses' } },
        { path: 'clients-points', component: () => import('../pages/ClientsPoints.vue'), meta: { permissionKey: 'clients-points' } },
        { path: 'clients-points/add', component: () => import('../pages/AddClientsPoints.vue'), meta: { permissionKey: 'clients-points' } },
        { path: 'clients-points/update/:id', component: () => import('../pages/AddClientsPoints.vue'), meta: { permissionKey: 'clients-points' } },

        // { path: 'deals', component: () => import('../pages/Deals.vue') },
        // { path: 'settings', component: () => import('../pages/Settings.vue') }
      ]
    }
  ]
});

const permissionMap = {
  home: "Home",
  users: "User",
  products: "Product",
  expiredProducts: "ExpiredProduct",
  returnProduct: "ReturnProduct",
  categories: "Category",
  sell: "Sell",
  inventory: "Inventory",
  reports: "Report",
  expenses: "Expense",
  "clients-points": "ClientPoint",
  deals: "Discount",
};


router.beforeEach((to, from, next) => {
  const isAuthenticated = store.getters.isAuthenticated;
  const permissions = store.state.user?.permission || [];
  const isAdmin = store.state?.user?.role_id === 1;

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isAuthenticated) {
      return next('/login');
    }

    // Block specific routes for non-admins
    const isBlockedAdminOnly = ['/dashboard/sell/update'].some(path =>
      to.path.startsWith(path)
    );
    if (!isAdmin && isBlockedAdminOnly) {
      return next('/dashboard/home');
    }

    // Check permission if `permissionKey` is defined
    const permissionKey = to.meta.permissionKey;
    if (permissionKey) {
      const backendKey = permissionMap[permissionKey];
      const permissionObj = permissions.find(p => p.name === backendKey);
      if (to.path === '/dashboard/home' && (!permissionObj || permissionObj.permission === 0)) {
        return next('/dashboard/no-permission');
      }
      if (permissionObj && permissionObj.permission === 0) {
        return next('/dashboard/home'); // or a 403 page
      }
    }
  }

  next();
});


export default router;